import { AsyncPipe } from '@angular/common'
import { Component, DestroyRef, inject, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, NavigationEnd, NavigationSkipped, NavigationStart, Router } from '@angular/router'
import { User } from '@ftr/contracts/api/user'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  BreadcrumbService,
  ButtonComponent,
  IconLegacyComponent,
  isMobileScreenSize,
  ScreenSize,
  ScreenSizeService,
  TooltipModule,
  unwrapData,
} from '@ftr/foundation'
import { UserAvatarComponent, UserState } from '@ftr/ui-user'
import { NgbPopover, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngxs/store'
import { Options } from '@popperjs/core'
import { Observable } from 'rxjs'
import { PersonalMenuComponent } from '../personal-menu/personal-menu.component'

@Component({
  selector: 'ftr-account-dropdown',
  templateUrl: './account-dropdown.component.html',
  styleUrls: ['./account-dropdown.component.css'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgbPopoverModule,
    IconLegacyComponent,
    UserAvatarComponent,
    AsyncPipe,
    ButtonComponent,
    PersonalMenuComponent,
    TooltipModule,
  ],
})
export class AccountDropdownComponent implements OnInit {
  private readonly store = inject(Store)
  private readonly screenSizeService = inject(ScreenSizeService)
  private readonly router = inject(Router)
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly breadcrumbService = inject(BreadcrumbService)

  @Input() currentCourtSystemId: Uuid | undefined
  @ViewChild('personalMenuPopOver') popover: NgbPopover

  user$: Observable<User>
  showingBreadcrumbs = false

  private focusTimeout: ReturnType<typeof setTimeout>
  protected readonly ftrLogoPath = '/assets/images/logo-themed-no-text.svg'

  protected readonly popperOptions = (options: Partial<Options>): Partial<Options> => {
    // Use default options for desktop
    if (!isMobileScreenSize(this.currentScreenSize)) {
      return options
    }
    // Delegate positioning to custom css class
    options.modifiers = [
      {
        name: 'applyStyles',
        enabled: false,
      },
    ]
    return options
  }

  @ViewChild(NgbPopover)
  protected personalMenuPopover: NgbPopover

  private currentScreenSize: ScreenSize

  readonly #destroyRef = inject(DestroyRef)

  ngOnInit(): void {
    this.user$ = this.store.select(UserState.user).pipe(unwrapData())
    this.watchScreenSize()
    this.watchRouterEvents()
  }

  onFocusIn(): void {
    clearTimeout(this.focusTimeout)
  }

  // Hide popover when focus is lost
  onFocusOut(): void {
    this.focusTimeout = setTimeout(() => {
      if (this.popover) {
        this.popover.close()
      }
    }, 0)
  }

  private watchScreenSize(): void {
    this.screenSizeService.size.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(size => {
      const shouldReopen = !!this.personalMenuPopover?.isOpen() && this.currentScreenSize !== size
      this.currentScreenSize = size
      // Popper modifiers/styles aren't dynamic, that's why we close the popover when
      // resizing to and from isMobile for a smoother experience
      if (shouldReopen) {
        this.personalMenuPopover.close()
        this.personalMenuPopover.open()
      }
    })
  }

  get personalMenuText(): string {
    const isOpen = this.personalMenuPopover?.isOpen() ?? false
    return `${isOpen ? 'Opened' : 'Closed'} Personal Menu, Select to ${isOpen ? 'Close' : 'Open'}`
  }

  private watchRouterEvents(): void {
    // Close the personal menu if we try to navigate away from the current page
    this.router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(event => {
      if (
        (event instanceof NavigationStart || event instanceof NavigationSkipped) &&
        this.personalMenuPopover.isOpen()
      ) {
        this.personalMenuPopover.close()
      }
      if (event instanceof NavigationEnd) {
        this.showingBreadcrumbs = this.breadcrumbService.isShowingBreadcrumbs(this.activatedRoute)
      }
    })
  }
}
